import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';
import { PerformanceInfoData } from '@features/models/performance-info-data';
import { ProductionGamePageService } from '@features/games/services/production-game-page.service';
import { GameRankingPuck } from './models/game-ranking-puck';
import { GameRankingService } from '@features/games/services/game-ranking.service';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { RatingPriority } from './models/rating-priority';
import { GameRankingData } from '@features/games/models/game-ranking-data';
import { GameRankingChangeData } from '@features/games/models/game-ranking-change-data';

@Component({
  selector: 'app-production-game-rank',
  imports: [CommonModule, DragDropModule],
  templateUrl: './production-game-rank.component.html',
  styleUrl: './production-game-rank.component.scss'
})
export class ProductionGameRankComponent {

  private productionGamePageService = inject(ProductionGamePageService);
  private gameRankingService = inject(GameRankingService);
  
  private gameDetailsIdSubject$ = new BehaviorSubject<string | undefined>(undefined);

  public get gameDetailsId$() : Observable<string | undefined> {
    return this.gameDetailsIdSubject$.asObservable();
  }

  private ratingPriorityList = new Array<RatingPriority>();
  private gameId?: string;
  public performanceList?: PerformanceInfoData[];
  public gameRankingPuckList?: GameRankingPuck[];
  private draggedPuck?: GameRankingPuck;
  public autoMove: boolean = true;

  constructor() {
    this.ratingPriorityList.push({ rating: 'love', priority: 0 });
    this.ratingPriorityList.push({ rating: 'like', priority: 1 });
    this.ratingPriorityList.push({ rating: 'loathe', priority: 2 });
  }

  ngOnInit(): void {

    this.productionGamePageService.page$.subscribe(page => {
      if (!page?.productionDetails?.performanceList || !page.gameDetails?.id) return;

      this.performanceList = page.productionDetails?.performanceList?.sort((a: PerformanceInfoData, b: PerformanceInfoData) => (a.sequenceNo ?? 0) - (b.sequenceNo ?? 0));
      this.gameId = page.gameDetails.gameId;
      
      this.gameDetailsIdSubject$.next(page.gameDetails?.id);
    });

    this.gameDetailsId$.subscribe(gameDetailsId => {
      if (!gameDetailsId) return;

      this.gameRankingService.getRankingDetails(gameDetailsId).subscribe(gameRankingDetails => {
        if (!gameRankingDetails?.gameRankingList) return;
        
        const gameRankingPuckList: (GameRankingPuck | undefined)[] = new Array(this.performanceList!.length).fill(undefined);

        gameRankingDetails.gameRankingList.forEach(gameRanking => {
          var performance = this.performanceList!.find(performance => performance.performanceId == gameRanking.performanceId);

          var gameRankingPuck = {
            id: gameRanking.id,
            performanceId: gameRanking.performanceId,
            performerName: performance?.performerName ?? "",
            songName: performance?.songName ?? "",
            countryName: performance?.representationCountryName ?? "",
            rating: gameRanking.rating,
            ranking: gameRanking.ranking
          };

          gameRankingPuckList[gameRanking.ranking] = gameRankingPuck;
        });

        var index = 0;
        this.performanceList!.forEach(performance => {

          var gameRankingPuck = gameRankingPuckList.find(gameRankPuck => gameRankPuck?.performanceId == performance.performanceId);

          if (!gameRankingPuck) {

            while (gameRankingPuckList[index]) {
              index++;
            }

            gameRankingPuck = {
              id: undefined,
              performanceId: performance.performanceId,
              performerName: performance.performerName ?? "",
              songName: performance.songName ?? "",
              countryName: performance.representationCountryName ?? "",
              rating: undefined,
              ranking: index
            };

            gameRankingPuckList[index] = gameRankingPuck;
          }
        });

        this.gameRankingPuckList = gameRankingPuckList.map(gameRankingPuck => gameRankingPuck!);
      });
    });

  }

  public resetRankings() {
    this.gameRankingPuckList = new Array<GameRankingPuck>();

    this.performanceList!.forEach(performance => {

      var gameRankingPuck = {
        id: undefined,
        performanceId: performance.performanceId,
        performerName: performance.performerName ?? "",
        songName: performance.songName ?? "",
        countryName: performance.representationCountryName ?? "",
        rating: undefined,
        ranking: performance.sequenceNo! - 1
      };

      this.gameRankingPuckList?.push(gameRankingPuck);
    });
  }

  public ratePerformance(performanceId: string, rating: string) {
    if (!this.gameId || !this.gameRankingPuckList) return;

    var changedGameRankingPuckList = new Array<GameRankingPuck>();
    var gameRankingPuck = this.gameRankingPuckList.find(gameRankingPuck => gameRankingPuck.performanceId === performanceId);

    if (gameRankingPuck && gameRankingPuck?.rating !== rating) {
      var autoSortApplies = gameRankingPuck.rating === undefined;

      gameRankingPuck!.rating = rating;

      changedGameRankingPuckList.push(gameRankingPuck);

      if (autoSortApplies) { 
        changedGameRankingPuckList.push(...this.setRank(gameRankingPuck));
      }
    }

    if (changedGameRankingPuckList.length > 0) {
      const gameRankingChange: GameRankingChangeData = {
        gameRankingList: this.GameRankingPuckListToGameRankingData(changedGameRankingPuckList)
      }
      this.UpdateGameRatings(gameRankingChange);
    }
  }

  private setRank(gameRankingPuck: GameRankingPuck): GameRankingPuck[] {
    if (!this.gameRankingPuckList || !this.autoMove) return [];

    var changedGameRankingPuckList = new Array<GameRankingPuck>();
    var ratingPriority = this.ratingPriorityList.find(priority => priority.rating === gameRankingPuck.rating)!;

    var gameRankingPuckIndex = gameRankingPuck.ranking!;
    var moveUpToIndex: (number | undefined) = undefined; 
    var moveDownToIndex: (number | undefined) = undefined;

    for (let index = gameRankingPuckIndex - 1; index >= 0; index--) {
      var rating = this.gameRankingPuckList[index].rating;
      
      if (rating) {
        var otherRatingPriority = this.ratingPriorityList.find(priority => priority.rating === rating)!;
      
        if (otherRatingPriority.priority > ratingPriority.priority) {
          moveUpToIndex = index;
        }
      }
    }

    for (let index = gameRankingPuckIndex + 1; index <= this.performanceList!.length - 1; index++) {
      var rating = this.gameRankingPuckList[index].rating;
      
      if (rating) {
        var otherRatingPriority = this.ratingPriorityList.find(priority => priority.rating === rating)!;
        
        if (otherRatingPriority.priority < ratingPriority.priority) {
          moveDownToIndex = index;
        }
      }
    }

    if (moveUpToIndex) {
      changedGameRankingPuckList.push(...this.MoveGameRankingPuck(gameRankingPuckIndex, moveUpToIndex));
      gameRankingPuck.ranking = moveUpToIndex;
      this.gameRankingPuckList[moveUpToIndex] = gameRankingPuck;
    }
    else if (moveDownToIndex) {
      changedGameRankingPuckList.push(...this.MoveGameRankingPuck(gameRankingPuckIndex, moveDownToIndex));
      gameRankingPuck.ranking = moveDownToIndex;
      this.gameRankingPuckList[moveDownToIndex] = gameRankingPuck;
    }

    return changedGameRankingPuckList;
  }

  public onDrag(index: number) {
    if (!this.gameRankingPuckList) return;
    this.draggedPuck = this.gameRankingPuckList[index];
  }

  public onDrop(event: CdkDragDrop<GameRankingPuck[]>) {
    if (!this.gameRankingPuckList || !this.draggedPuck) return;

    var changedGameRankingPuckList = new Array<GameRankingPuck>();
    const vacatedIndex = event.previousIndex;
    const toOccupyIndex = event.currentIndex;
    
    changedGameRankingPuckList.push(...this.MoveGameRankingPuck(vacatedIndex, toOccupyIndex));

    this.draggedPuck.ranking = toOccupyIndex;
    this.gameRankingPuckList[toOccupyIndex] = this.draggedPuck;
    changedGameRankingPuckList.push(this.draggedPuck);
    this.draggedPuck = undefined;

    if (changedGameRankingPuckList.length > 0) {
      const gameRankingChange: GameRankingChangeData = {
        gameRankingList: this.GameRankingPuckListToGameRankingData(changedGameRankingPuckList)
      }
      this.UpdateGameRatings(gameRankingChange);
    }
  }

  private MoveGameRankingPuck(vacatedIndex: number, toOccupyIndex: number): GameRankingPuck[] {
    if (!this.gameRankingPuckList) return[];

    var changedGameRankingPuckList = new Array<GameRankingPuck>();

    if (vacatedIndex < toOccupyIndex) {
      for (let index = vacatedIndex; index < toOccupyIndex; index++) {
        this.gameRankingPuckList[index] = this.gameRankingPuckList[index + 1];
        this.gameRankingPuckList[index]!.ranking = index;

        if (this.gameRankingPuckList[index].rating) {
          changedGameRankingPuckList.push(this.gameRankingPuckList[index]!);
        }
      }
    }
    else {
      for (let index = vacatedIndex; index > toOccupyIndex; index--) {
        this.gameRankingPuckList[index] = this.gameRankingPuckList[index - 1];
        this.gameRankingPuckList[index]!.ranking = index;

        if (this.gameRankingPuckList[index].rating) {  
          changedGameRankingPuckList.push(this.gameRankingPuckList[index]!);
        }
      }
    }

    return changedGameRankingPuckList;
  }

  private UpdateGameRatings(gameRankingChange: GameRankingChangeData) {
    this.gameRankingService.setRankingList(gameRankingChange).subscribe(changed => {
      if (changed) {
        changed.gameRankingList.forEach((gameRankingChange) => {
          var gameRankingPuck = this.gameRankingPuckList?.find(gameRankingPuck => gameRankingPuck.performanceId === gameRankingChange.performanceId);
          if (gameRankingPuck) {
            gameRankingPuck.id = gameRankingChange.id;
          }
        });
      }
    });
  }

  private GameRankingPuckToGameRankingData(gameRankingPuck: GameRankingPuck): GameRankingData {
    return {
      id: gameRankingPuck.id,
      gameId: this.gameId!,
      performanceId: gameRankingPuck.performanceId,
      rating: gameRankingPuck.rating!,
      ranking: gameRankingPuck.ranking!
    };
  }

  private GameRankingPuckListToGameRankingData(gameRankingPuckList: GameRankingPuck[]): GameRankingData[] {
    var gameRankingList = new Array<GameRankingData>();

    gameRankingPuckList.forEach((gameRankingPuck, index) => {
      gameRankingList.push(this.GameRankingPuckToGameRankingData(gameRankingPuck))
    });

    return gameRankingList;
  }

}
